import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  Box,
  Flex,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Badge,
  Divider,
  Select,
  IconButton,
} from "@chakra-ui/react";
import { SearchIcon, ChevronDownIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { TailSpin } from "react-loader-spinner";
import { API_URL } from "../../../constants";
import { getAuthToken } from "../../../helpers/helpers";

const DEFAULT_NEAR_EXPIRY_MONTHS = 6;

const GROUP_OPTIONS = [
  { value: "none", label: "Aucun regroupement" },
  { value: "labo", label: "Laboratoire" },
  { value: "dci", label: "DCI" },
];

export default function ConfigProPerime() {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [groupBy, setGroupBy] = useState("none");

  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const [collapseSignal, setCollapseSignal] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [editMonths, setEditMonths] = useState(DEFAULT_NEAR_EXPIRY_MONTHS);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editGroupMonths, setEditGroupMonths] = useState(DEFAULT_NEAR_EXPIRY_MONTHS);
  const [isSaving, setIsSaving] = useState(false);
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    if (!alert) return;
    const t = setTimeout(() => setAlert(null), 4000);
    return () => clearTimeout(t);
  }, [alert]);

  useEffect(() => {
    fetchInventory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupBy]);

  useEffect(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) {
      setFilteredProducts(products);
    } else {
      setFilteredProducts(
        products.filter((p) =>
          (p.specialite || "").toLowerCase().includes(q)
        )
      );
    }
  }, [searchQuery, products]);

  const fetchInventory = async () => {
    setIsLoading(true);
    const token = getAuthToken();
    try {
      const response = await axios.get(`${API_URL}/api/inventory`, {
        params: groupBy !== "none" ? { groupBy } : {}, 
        headers: {
          "x-access-token": token,
          Authorization: `Bearer ${token}`,
        },
      });
      const inStock = (response.data || []).filter(
        (item) => !item.masked && item.en_stock !== false
      );
      setProducts(inStock);
      setFilteredProducts(inStock);
      // Signal that fresh data arrived so groups can be collapsed by default.
      setCollapseSignal((s) => s + 1);
    } catch (error) {
      setAlert({
        title: "Erreur",
        description: "Impossible de charger l'inventaire.",
        status: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const groupedData = useMemo(() => {
    if (groupBy === "none") return null;

    const groups = {};
    filteredProducts.forEach((p) => {
      // Clean up the key and ensure "Autre" is the consistent fallback
      const val = p[groupBy];
      const key = (val && typeof val === 'string') ? val.trim() : "";
      const finalKey = key || "Autre";

      if (!groups[finalKey]) groups[finalKey] = [];
      groups[finalKey].push(p);
    });

    return Object.entries(groups).sort(([a], [b]) => {
      // Force "Autre" to the very bottom
      if (a === "Autre" && b !== "Autre") return 1;
      if (a !== "Autre" && b === "Autre") return -1;
      
      // Standard alphabetical sort for everything else
      return a.localeCompare(b);
    });
  }, [filteredProducts, groupBy]);

  const allGroupKeys = useMemo(
    () => (groupedData ? groupedData.map(([key]) => key) : []),
    [groupedData]
  );

  const allCollapsed =
    allGroupKeys.length > 0 &&
    allGroupKeys.every((key) => collapsedGroups.has(key));

  // Collapse every group by default whenever fresh inventory is loaded
  // (page load or when the grouping mode changes).
  useEffect(() => {
    if (groupBy === "none") return;
    setCollapsedGroups(new Set(allGroupKeys));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapseSignal]);

  const toggleGroup = (key) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAllGroups = () => {
    setCollapsedGroups(allCollapsed ? new Set() : new Set(allGroupKeys));
  };

  // ── Single product edit ──────────────────────────────────────────────────

  const openEdit = (product) => {
    setEditingGroup(null);
    setEditingId(product._id);
    setEditMonths(
      product.nearExpiryMonths != null ? product.nearExpiryMonths : ""
    );
  };

  const cancelEdit = () => setEditingId(null);

  const confirmEdit = async (product) => {
    const months = editMonths === "" ? DEFAULT_NEAR_EXPIRY_MONTHS : Number(editMonths);
    if (!Number.isInteger(months) || months < 1 || months > 24) {
      setAlert({
        title: "Valeur invalide",
        description: "La période doit être un entier entre 1 et 24 (en mois).",
        status: "warning",
      });
      return;
    }
    setIsSaving(true);
    const token = getAuthToken();
    try {
      await axios.patch(
        `${API_URL}/api/inventory/${product._id}/near-expiry`,
        { nearExpiryMonths: months },
        {
          headers: {
            "x-access-token": token,
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setProducts((prev) =>
        prev.map((p) =>
          p._id === product._id ? { ...p, nearExpiryMonths: months } : p
        )
      );
      setAlert({
        title: "Mis à jour",
        description: `"${product.specialite}" : période mise à jour à ${months} mois.`,
        status: "success",
      });
      setEditingId(null);
    } catch {
      setAlert({
        title: "Erreur",
        description: "Impossible de mettre à jour la période.",
        status: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const resetToDefault = async (product) => {
    setIsSaving(true);
    const token = getAuthToken();
    try {
      await axios.patch(
        `${API_URL}/api/inventory/${product._id}/near-expiry`,
        { nearExpiryMonths: DEFAULT_NEAR_EXPIRY_MONTHS },
        {
          headers: {
            "x-access-token": token,
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setProducts((prev) =>
        prev.map((p) =>
          p._id === product._id
            ? { ...p, nearExpiryMonths: DEFAULT_NEAR_EXPIRY_MONTHS }
            : p
        )
      );
      setEditingId(null);
      setAlert({
        title: "Réinitialisé",
        description: `Période de "${product.specialite}" réinitialisée à ${DEFAULT_NEAR_EXPIRY_MONTHS} mois.`,
        status: "success",
      });
    } catch {
      setAlert({
        title: "Erreur",
        description: "Impossible de réinitialiser la période.",
        status: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // ── Group-level bulk edit ────────────────────────────────────────────────

  const openGroupEdit = (groupKey, e) => {
    e.stopPropagation();
    setEditingId(null);
    setEditingGroup(groupKey);
    setEditGroupMonths(DEFAULT_NEAR_EXPIRY_MONTHS);
  };

  const cancelGroupEdit = () => setEditingGroup(null);

  const confirmGroupEdit = async (groupKey, groupProducts) => {
    const months = editGroupMonths === "" ? DEFAULT_NEAR_EXPIRY_MONTHS : Number(editGroupMonths);
    if (!Number.isInteger(months) || months < 1 || months > 24) {
      setAlert({
        title: "Valeur invalide",
        description: "La période doit être un entier entre 1 et 24 (en mois).",
        status: "warning",
      });
      return;
    }
    setIsSaving(true);
    const token = getAuthToken();
    try {
      await Promise.all(
        groupProducts.map((p) =>
          axios.patch(
            `${API_URL}/api/inventory/${p._id}/near-expiry`,
            { nearExpiryMonths: months },
            {
              headers: {
                "x-access-token": token,
                Authorization: `Bearer ${token}`,
              },
            }
          )
        )
      );
      const ids = new Set(groupProducts.map((p) => p._id));
      setProducts((prev) =>
        prev.map((p) =>
          ids.has(p._id) ? { ...p, nearExpiryMonths: months } : p
        )
      );
      setAlert({
        title: "Groupe mis à jour",
        description: `${groupProducts.length} produit(s) du groupe "${groupKey}" mis à jour à ${months} mois.`,
        status: "success",
      });
      setEditingGroup(null);
    } catch {
      setAlert({
        title: "Erreur",
        description: "Impossible de mettre à jour certains produits du groupe.",
        status: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const resetGroupToDefault = async (groupKey, groupProducts, e) => {
    e.stopPropagation();
    setIsSaving(true);
    const token = getAuthToken();
    try {
      await Promise.all(
        groupProducts.map((p) =>
          axios.patch(
            `${API_URL}/api/inventory/${p._id}/near-expiry`,
            { nearExpiryMonths: DEFAULT_NEAR_EXPIRY_MONTHS },
            {
              headers: {
                "x-access-token": token,
                Authorization: `Bearer ${token}`,
              },
            }
          )
        )
      );
      const ids = new Set(groupProducts.map((p) => p._id));
      setProducts((prev) =>
        prev.map((p) =>
          ids.has(p._id)
            ? { ...p, nearExpiryMonths: DEFAULT_NEAR_EXPIRY_MONTHS }
            : p
        )
      );
      setEditingGroup(null);
      setAlert({
        title: "Groupe réinitialisé",
        description: `${groupProducts.length} produit(s) du groupe "${groupKey}" réinitialisés à ${DEFAULT_NEAR_EXPIRY_MONTHS} mois.`,
        status: "success",
      });
    } catch {
      setAlert({
        title: "Erreur",
        description: "Impossible de réinitialiser certains produits du groupe.",
        status: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const groupHasCustom = (groupProducts) =>
    groupProducts.some(
      (p) =>
        p.nearExpiryMonths != null &&
        p.nearExpiryMonths !== DEFAULT_NEAR_EXPIRY_MONTHS
    );

  // ── Render helpers ───────────────────────────────────────────────────────

  const renderProductRow = (product) => {
    const months =
      product.nearExpiryMonths != null
        ? product.nearExpiryMonths
        : DEFAULT_NEAR_EXPIRY_MONTHS;
    const isCustom =
      product.nearExpiryMonths != null &&
      product.nearExpiryMonths !== DEFAULT_NEAR_EXPIRY_MONTHS;
    const isEditing = editingId === product._id;

    return (
      <React.Fragment key={product._id}>
        <Tr
          cursor="pointer"
          bg={isEditing ? "blue.50" : "white"}
          _hover={{ bg: isEditing ? "blue.50" : "gray.50" }}
          onClick={() => {
            if (isEditing) cancelEdit();
            else openEdit(product);
          }}
          transition="background 0.15s"
        >
          <Td pl={groupBy !== "none" ? 8 : 4}>
            <Text fontWeight="medium" color="navy.700">
              {product.specialite || "—"}
            </Text>
            {product.forme && (
              <Text fontSize="xs" color="gray.500">
                {product.forme}
              </Text>
            )}
          </Td>
          <Td isNumeric>
            <Flex justify="flex-end" align="center" gap={2}>
              <Text fontWeight="semibold" color="teal.600">
                {months} mois
              </Text>
              {isCustom && (
                <>
                  <Badge colorScheme="orange" fontSize="0.65em">
                    Personnalisé
                  </Badge>
                  <Button
                    size="xs"
                    variant="link"
                    colorScheme="gray"
                    onClick={(e) => {
                      e.stopPropagation();
                      resetToDefault(product);
                    }}
                    isDisabled={isSaving}
                    ml={2}
                  >
                    Réinitialiser
                  </Button>
                </>
              )}
            </Flex>
          </Td>
          <Td isNumeric>
            <Text color="gray.700">{product.qte_stock ?? "—"}</Text>
          </Td>
        </Tr>

        {isEditing && (
          <Tr bg="blue.50">
            <Td colSpan={3} pt={2} pb={4}>
              <Divider mb={3} borderColor="blue.100" />
              <Flex align="flex-end" gap={4} flexWrap="wrap">
                <Box>
                  <Text fontSize="xs" color="gray.500" fontWeight="medium" mb={1}>
                    Période pro-périmé (mois)
                  </Text>
                  <NumberInput
                    min={1}
                    max={24}
                    value={editMonths}
                    onChange={(valueString, valueAsNumber) => {
                      if (valueString === "") {
                        setEditMonths("");
                        return;
                      }
                      const v = Math.floor(valueAsNumber || 0);
                      setEditMonths(Math.max(1, Math.min(24, v)));
                    }}
                    size="sm"
                    w="120px"
                  >
                    <NumberInputField bg="white" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <Text fontSize="xs" color="gray.400" mt={1}>
                    Laisser vide = revenir à {DEFAULT_NEAR_EXPIRY_MONTHS} mois.
                  </Text>
                </Box>
                <Flex gap={2} mb="22px">
                  <Button
                    size="sm"
                    colorScheme="teal"
                    isLoading={isSaving}
                    loadingText="…"
                    onClick={(e) => {
                      e.stopPropagation();
                      confirmEdit(product);
                    }}
                  >
                    Confirmer
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    isDisabled={isSaving}
                    onClick={(e) => {
                      e.stopPropagation();
                      cancelEdit();
                    }}
                  >
                    Annuler
                  </Button>
                </Flex>
              </Flex>
            </Td>
          </Tr>
        )}
      </React.Fragment>
    );
  };

  const renderGroupRows = ([groupKey, groupProducts]) => {
    const isCollapsed = collapsedGroups.has(groupKey);
    const isEditingThisGroup = editingGroup === groupKey;
    const hasCustom = groupHasCustom(groupProducts);

    return (
      <React.Fragment key={groupKey}>
        <Tr
          bg="gray.100"
          cursor="pointer"
          _hover={{ bg: "gray.200" }}
          onClick={() => toggleGroup(groupKey)}
          transition="background 0.15s"
        >
          <Td colSpan={3} py={2} px={4}>
            <Flex align="center" justify="space-between">
              <Flex align="center" gap={2}>
                <IconButton
                  icon={isCollapsed ? <ChevronRightIcon /> : <ChevronDownIcon />}
                  size="xs"
                  variant="ghost"
                  aria-label="Expand/collapse"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleGroup(groupKey);
                  }}
                />
                <Text fontWeight="bold" color="navy.700" fontSize="sm">
                  {groupKey}
                </Text>
                <Badge colorScheme="blue" fontSize="0.65em" ml={1}>
                  {groupProducts.length} produit{groupProducts.length > 1 ? "s" : ""}
                </Badge>
                {hasCustom && (
                  <Badge colorScheme="orange" fontSize="0.65em">
                    Personnalisé
                  </Badge>
                )}
              </Flex>

              <Flex gap={2} align="center" onClick={(e) => e.stopPropagation()}>
                {hasCustom && (
                  <Button
                    size="xs"
                    variant="outline"
                    colorScheme="gray"
                    isDisabled={isSaving}
                    onClick={(e) => resetGroupToDefault(groupKey, groupProducts, e)}
                  >
                    Réinitialiser le groupe
                  </Button>
                )}
                <Button
                  size="xs"
                  colorScheme="teal"
                  variant="outline"
                  onClick={(e) => openGroupEdit(groupKey, e)}
                >
                  Modifier le groupe
                </Button>
              </Flex>
            </Flex>
          </Td>
        </Tr>

        {isEditingThisGroup && (
          <Tr bg="teal.50">
            <Td colSpan={3} pt={2} pb={4} px={6}>
              <Divider mb={3} borderColor="teal.100" />
              <Text fontSize="sm" fontWeight="medium" color="teal.700" mb={2}>
                Appliquer à tous les {groupProducts.length} produits du groupe «{groupKey}»
              </Text>
              <Flex align="flex-end" gap={4} flexWrap="wrap">
                <Box>
                  <Text fontSize="xs" color="gray.500" fontWeight="medium" mb={1}>
                    Période pro-périmé (mois)
                  </Text>
                  <NumberInput
                    min={1}
                    max={24}
                    value={editGroupMonths}
                    onChange={(valueString, valueAsNumber) => {
                      if (valueString === "") {
                        setEditGroupMonths("");
                        return;
                      }
                      const v = Math.floor(valueAsNumber || 0);
                      setEditGroupMonths(Math.max(1, Math.min(24, v)));
                    }}
                    size="sm"
                    w="120px"
                  >
                    <NumberInputField bg="white" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <Text fontSize="xs" color="gray.400" mt={1}>
                    Laisser vide = revenir à {DEFAULT_NEAR_EXPIRY_MONTHS} mois.
                  </Text>
                </Box>
                <Flex gap={2} mb="22px">
                  <Button
                    size="sm"
                    colorScheme="teal"
                    isLoading={isSaving}
                    loadingText="…"
                    onClick={() => confirmGroupEdit(groupKey, groupProducts)}
                  >
                    Appliquer au groupe
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    isDisabled={isSaving}
                    onClick={cancelGroupEdit}
                  >
                    Annuler
                  </Button>
                </Flex>
              </Flex>
            </Td>
          </Tr>
        )}

        {!isCollapsed && groupProducts.map(renderProductRow)}
      </React.Fragment>
    );
  };

  return (
    <Flex flexDirection="column" pt="80px" px={{ base: 4, md: 6 }} minHeight="100vh">
      <Box mb={6}>
        <Text fontSize="sm" color="gray.500" mt={1}>
          Définissez la période pro-périmé (en mois) pour chaque produit de
          votre inventaire. Par défaut : {DEFAULT_NEAR_EXPIRY_MONTHS} mois.
        </Text>
      </Box>

      {alert && (
        <Box
          mb={4}
          p={3}
          borderRadius="md"
          bg={
            alert.status === "success"
              ? "green.50"
              : alert.status === "error"
              ? "red.50"
              : "yellow.50"
          }
        >
          <Text fontWeight="semibold" color="navy.700">
            {alert.title}
          </Text>
          <Text fontSize="sm" color="gray.600">
            {alert.description}
          </Text>
        </Box>
      )}

      <Flex gap={3} mb={4} flexWrap="wrap" align="center">
        <InputGroup maxW="360px">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Rechercher un produit…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            bg="white"
            borderRadius="md"
            boxShadow="sm"
          />
        </InputGroup>

        <Select
          maxW="220px"
          bg="white"
          borderRadius="md"
          boxShadow="sm"
          value={groupBy}
          onChange={(e) => {
            setGroupBy(e.target.value);
            setEditingId(null);
            setEditingGroup(null);
          }}
          size="md"
        >
          {GROUP_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>

        {groupBy !== "none" && allGroupKeys.length > 0 && (
          <Button
            size="md"
            variant="outline"
            colorScheme="blue"
            bg="white"
            boxShadow="sm"
            leftIcon={allCollapsed ? <ChevronDownIcon /> : <ChevronRightIcon />}
            onClick={toggleAllGroups}
          >
            {allCollapsed ? "Tout développer" : "Tout réduire"}
          </Button>
        )}
      </Flex>

      {isLoading ? (
        <Flex justify="center" align="center" minH="300px">
          <Box textAlign="center">
            <TailSpin
              height="80"
              width="80"
              color="#17254C"
              ariaLabel="tail-spin-loading"
              visible={true}
            />
            <Text mt={4} color="#17254C" fontSize="14px">
              Chargement de l'inventaire…
            </Text>
          </Box>
        </Flex>
      ) : filteredProducts.length === 0 ? (
        <Box bg="white" borderRadius="xl" boxShadow="sm" p={8} textAlign="center">
          <Text color="gray.500" fontSize="lg">
            {searchQuery
              ? "Aucun produit ne correspond à votre recherche."
              : "Votre inventaire est vide."}
          </Text>
        </Box>
      ) : (
        <Box bg="white" borderRadius="xl" boxShadow="sm" overflow="hidden">
          <Box overflowX="auto">
            <Table variant="simple" size="md">
              <Thead bg="gray.50">
                <Tr>
                  <Th>Produit</Th>
                  <Th isNumeric>Période pro-périmé</Th>
                  <Th isNumeric>Quantité en stock</Th>
                </Tr>
              </Thead>
              <Tbody>
                {groupBy === "none"
                  ? filteredProducts.map(renderProductRow)
                  : groupedData.map(renderGroupRows)}
              </Tbody>
            </Table>
          </Box>
        </Box>
      )}
    </Flex>
  );
}